import { Box, Heading, Icon, SimpleGrid, Text } from "@chakra-ui/react";
import { keyBy } from "lodash";
import { MdArrowForward } from "react-icons/md";
import { gql } from "__generated__/gql";
import { AccessPlanType } from "__generated__/gql/graphql";
import { serverGql } from "__generated__/serverGql";
import useCreateStripeSubscriptionCheckoutSession from "components/Core/AccountInfo/Billing/PlanSelection/useCreateStripeSubscriptionCheckoutSession";
import useCreateStripeSubscriptionUpdateSession from "components/Core/AccountInfo/Billing/PlanSelection/useCreateStripeSubscriptionUpdateSession";
import LoginLayoutButton from "components/Layout/LoginLayoutButton";
import Link from "components/UI/Links/Link";
import PageLoadingSpinner from "components/UI/Loading/PageLoadingSpinner";
import { useQuery } from "lib/apollo/hooks";
import { useBrandQuery } from "lib/apollo/hooks/brand";
import asyncVoidHandler from "lib/errors/asyncVoidHandler";
import serverClient from "lib/apollo/serverClient";
import { assertPresence } from "utils/assertions";
import { PRICING_PAGE } from "runtime-config/config";
import { useCurrentBrandId } from "lib/auth/currentUserHooks";
import { BRAND_BILLING, BRAND_INDEX, SIGNUP } from "lib/routing/links";
import { getIsSignupPlanSelectionCompleted } from "./metadata";
import SignupPlanCard from "./SignupPlanCard";
import useCompleteSignupPlanSelection from "./useCompleteSignupPlanSelection";
import useSignupPlanSelectionState from "./useSignupPlanSelectionState";

const query = gql(/* GraphQL */ `
  query SignupPlanSelection($brandId: UUID!) {
    brand(id: $brandId) {
      id
      accessPlanId
      metadata
      stripeSubscriptionId
    }
    accessPlansList(
      filter: { isSelectable: { equalTo: true } }
      orderBy: [SEQUENCE_ASC, NAME_ASC]
    ) {
      id
      ...SignupPlanCardAccessPlan
      ...SignupPlanSelectionStateAccessPlan
    }
  }
`);

const stripeProductsQuery = serverGql(/* ServerGraphQL */ `
  query SignupPlanSelectionStripeProducts {
    accessPlanStripeProducts {
      id
      ...SignupPlanCardStripeProduct
    }
  }
`);

type Props = {
  onContinue: () => Promise<void>;
  selectedPlanSlug?: string;
};

export default function SignupPlanSelection({
  onContinue,
  selectedPlanSlug,
}: Props) {
  const currentBrandId = useCurrentBrandId();

  const { data, loading: isAccessPlansLoading } = useBrandQuery(query, {
    skip: !currentBrandId,
    fetchPolicy: "cache-and-network",
    variables: currentBrandId ? { brandId: currentBrandId } : undefined,
  });
  const { data: stripeProductsData, loading: isStripeProductsLoading } =
    useQuery(stripeProductsQuery, {
      client: serverClient,
      fetchPolicy: "cache-and-network",
    });

  const {
    createStripeSubscriptionCheckoutSession,
    isLoading: isCheckoutSessionLoading,
  } = useCreateStripeSubscriptionCheckoutSession();

  const {
    createStripeSubscriptionUpdateSession,
    isLoading: isUpdateSessionLoading,
  } = useCreateStripeSubscriptionUpdateSession();

  const {
    completeSignupPlanSelection,
    isLoading: isCompleteSignupPlanSelectionLoading,
  } = useCompleteSignupPlanSelection();

  const accessPlans = data?.accessPlansList;
  const brand = data?.brand;
  const isCheckoutLoading =
    isCheckoutSessionLoading ||
    isUpdateSessionLoading ||
    isCompleteSignupPlanSelectionLoading;
  const isSignupPlanSelectionCompleted = getIsSignupPlanSelectionCompleted(
    brand?.metadata,
  );
  const stripeProductsById = keyBy(
    stripeProductsData?.accessPlanStripeProducts,
    "id",
  );

  const { selectedAccessPlan, setSelectedAccessPlanId } =
    useSignupPlanSelectionState({
      accessPlans,
      brandAccessPlanId: brand?.accessPlanId,
      selectedPlanSlug,
    });

  const canContinueWithSelectedPlan =
    !!selectedAccessPlan &&
    (selectedAccessPlan.planType === AccessPlanType.FREE ||
      !!selectedAccessPlan.stripeProductId);
  const isContinueDisabled =
    !brand || (!isSignupPlanSelectionCompleted && !canContinueWithSelectedPlan);

  const completeSelectedSignupPlanSelection = async () => {
    assertPresence(brand, "Expected signup brand for plan selection.");
    assertPresence(
      selectedAccessPlan,
      "Expected selected signup plan for plan selection.",
    );

    await completeSignupPlanSelection({
      accessPlanId: selectedAccessPlan.id,
      brandId: brand.id,
    });
    await onContinue();
  };

  const getContinueButtonText = () => {
    if (isSignupPlanSelectionCompleted) {
      return "Continue to App";
    }

    if (!brand || !selectedAccessPlan) {
      return "Choose a Plan";
    }

    if (
      selectedAccessPlan.planType !== AccessPlanType.FREE &&
      !selectedAccessPlan.stripeProductId
    ) {
      return "Contact Sales";
    }

    return `Continue with ${selectedAccessPlan.name}`;
  };

  const handleContinue = async () => {
    assertPresence(brand, "Expected signup brand for plan selection.");

    if (isSignupPlanSelectionCompleted) {
      await onContinue();
      return;
    }

    assertPresence(
      selectedAccessPlan,
      "Expected selected signup plan for plan selection.",
    );

    if (
      selectedAccessPlan.planType === AccessPlanType.FREE ||
      !selectedAccessPlan.stripeProductId
    ) {
      await completeSelectedSignupPlanSelection();
      return;
    }

    if (!brand.stripeSubscriptionId) {
      createStripeSubscriptionCheckoutSession({
        accessPlanId: selectedAccessPlan.id,
        brandId: brand.id,
        cancelPath: BRAND_INDEX,
        successPath: SIGNUP,
      });
      return;
    }

    if (selectedAccessPlan.id === brand.accessPlanId) {
      await completeSelectedSignupPlanSelection();
      return;
    }

    createStripeSubscriptionUpdateSession({
      accessPlanId: selectedAccessPlan.id,
      brandId: brand.id,
      returnPath: SIGNUP,
    });
  };

  if (!brand && isAccessPlansLoading) {
    return <PageLoadingSpinner minHeight="12rem" />;
  }

  return (
    <Box>
      <Heading as="h2" color="magenta.veryDark" mb={2} size="sm">
        {isSignupPlanSelectionCompleted ? "You’re All Set" : "Choose a Plan"}
      </Heading>

      <Text color="gray.600" fontSize="sm" mb={4}>
        {isSignupPlanSelectionCompleted ? (
          "Your workspace is already set up. Plan changes are handled from billing settings."
        ) : (
          <>
            You can continue with the selected plan now and change plans later
            from account settings. Want more information on what’s included? See
            our{" "}
            <Link href={PRICING_PAGE} isExternal fontWeight="semibold">
              pricing page
            </Link>{" "}
            for the full plan comparison.
          </>
        )}
      </Text>

      <SimpleGrid
        data-all-plan-cards-disabled={
          isSignupPlanSelectionCompleted ? true : undefined
        }
        minChildWidth="12rem"
        gap={4}
      >
        {accessPlans?.map((accessPlan) => (
          <SignupPlanCard
            key={accessPlan.id}
            accessPlan={accessPlan}
            isCurrent={accessPlan.id === brand?.accessPlanId}
            isPriceLoading={isAccessPlansLoading || isStripeProductsLoading}
            isSelected={accessPlan.id === selectedAccessPlan?.id}
            isSignupPlanSelectionCompleted={isSignupPlanSelectionCompleted}
            onSelect={setSelectedAccessPlanId}
            stripeProductsById={stripeProductsById}
          />
        ))}
      </SimpleGrid>

      <LoginLayoutButton
        isDisabled={isContinueDisabled}
        isLoading={isCheckoutLoading}
        mt={6}
        onClick={asyncVoidHandler(handleContinue)}
        rightIcon={<Icon as={MdArrowForward} mr={-2} />}
      >
        {getContinueButtonText()}
      </LoginLayoutButton>

      {isSignupPlanSelectionCompleted && (
        <Text color="gray.600" fontSize="sm" mt={4} textAlign="center">
          Need to change plans?{" "}
          <Link color="magenta.dark" fontWeight="semibold" href={BRAND_BILLING}>
            Manage billing
          </Link>
          .
        </Text>
      )}
    </Box>
  );
}
